import { Table } from 'antd';
import './CustomTable.less';

const CustomTable = ({
  columns,
  dataSource,
  selectedRowKeys,
  onSelectItem,
  setSelectedRowKeys,
  showCheckbox = true,
  isMultipleSelect = false,
  rowSelection: parentRowSelection, // parent rowSelection from caller (optional)
  rowKey = 'key',
  disableArchivedRows = false, // <-- NEW optional flag; defaults to off
  ...restProps
}) => {
  const isDisabled = (record) => {
    // If parent supplies getCheckboxProps, use it as source of truth
    const parentDisabled =
      typeof parentRowSelection?.getCheckboxProps === 'function'
        ? !!parentRowSelection.getCheckboxProps(record)?.disabled
        : false;

    if (parentDisabled) return true;

    // Optional internal rule: disable archived rows when flag is set
    if (disableArchivedRows) {
      const status = String(
        record?.status || record?.Status || ''
      ).toLowerCase();
      if (status === 'archived' || record?.disabled) return true;
    }

    return false;
  };

  const handleSelect = (currentItem) => {
    // Never toggle disabled rows
    if (isDisabled(currentItem)) return;

    // Fallback selection behavior (preserves current behavior where parent doesn't fully control)
    if (isMultipleSelect) {
      if (
        selectedRowKeys?.length &&
        selectedRowKeys?.includes(currentItem?.key)
      ) {
        const filter = selectedRowKeys?.filter(
          (item) => item !== currentItem?.key
        );
        setSelectedRowKeys && setSelectedRowKeys(filter);
      } else {
        const currentSelected = [
          ...(selectedRowKeys?.length > 0 ? selectedRowKeys : []),
          currentItem?.key,
        ];
        setSelectedRowKeys && setSelectedRowKeys(currentSelected);
      }
    } else {
      setSelectedRowKeys && setSelectedRowKeys([currentItem?.key]);
      onSelectItem && onSelectItem(currentItem);
    }
  };

  // Build a merged rowSelection that respects the parent's config, but enforces disabled rows
  const mergedRowSelection = showCheckbox && {
    selectedRowKeys,
    hideSelectAll: parentRowSelection?.hideSelectAll ?? true,
    ...parentRowSelection, // parent callbacks (onChange, onSelectAll, etc.) remain intact

    // Enforce disabled at the checkbox level (union with parent)
    getCheckboxProps: (record) => {
      const parentProps = parentRowSelection?.getCheckboxProps
        ? parentRowSelection.getCheckboxProps(record) || {}
        : {};
      return {
        ...parentProps,
        disabled: !!parentProps.disabled || isDisabled(record),
      };
    },

    // Guard onSelect to block any change for disabled rows. Then delegate to parent or fallback.
    onSelect: (record, selected, selectedRows, nativeEvent) => {
      if (isDisabled(record)) {
        nativeEvent?.preventDefault?.();
        nativeEvent?.stopPropagation?.();
        return;
      }
      if (typeof parentRowSelection?.onSelect === 'function') {
        parentRowSelection.onSelect(
          record,
          selected,
          selectedRows,
          nativeEvent
        );
      } else {
        handleSelect(record);
      }
    },
  };

  return (
    <div className='scroller' style={{ height: 'calc(100% - 10px)' }}>
      <Table
        virtual
        className='custom_table'
        scroll={{ y: 'auto', x: 1000 }}
        rowKey={rowKey}
        rowSelection={mergedRowSelection}
        columns={columns}
        dataSource={dataSource}
        pagination={false}
        onRow={(item) => ({
          onClick: () => {
            // Block row-click toggling for disabled rows
            if (isDisabled(item)) return;

            // If parent controls selection (passed rowSelection), let them handle via checkboxes/onChange
            // Otherwise preserve old behavior: row click toggles selection locally.
            if (!parentRowSelection) {
              handleSelect(item);
            }
          },
        })}
        {...restProps}
      />
    </div>
  );
};

export default CustomTable;
